import { isBatteryCategoryId, isBatteryProduct } from '@/utilities/getProductBaseCategory';
import { getProductBuyHref } from '@/utilities/getProductBuyHref';

import { CatalogProductCardItem } from './models';

import { AttributeItem, ProductType } from '@/server/services/core/product/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { OfferContent } from '@/server/services/vendor/models';

type FromProductOptions = {
    categoryId?: number;
};

const MAX_CARD_SPECS = 4;

export const formatAttributeSpec = (attr: AttributeItem): string => {
    const rawValue =
        attr.type === 'BOOLEAN' ? (attr.value === true ? 'بله' : attr.value === false ? 'خیر' : '') : attr.value == null ? '' : String(attr.value);

    if (!rawValue) {
        return '';
    }

    const unit = attr.unitName || attr.unitSymbol || '';
    const valueLabel = unit ? `${rawValue} ${unit}` : rawValue;

    return attr.name ? `${attr.name} ${valueLabel}` : valueLabel;
};

export const catalogSpecsFromAttributes = (attributes?: AttributeItem[] | null, limit = MAX_CARD_SPECS) => {
    if (!attributes?.length) {
        return undefined;
    }

    const specs = [...attributes]
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map(formatAttributeSpec)
        .filter(Boolean)
        .slice(0, limit);

    return specs.length ? specs : undefined;
};

export const catalogSpecsFromSubtitle = (subtitle?: string | null, limit = MAX_CARD_SPECS) => {
    if (!subtitle?.trim()) {
        return undefined;
    }

    const parts = subtitle
        .split(/،|,/)
        .map((part) => part.trim())
        .filter(Boolean)
        .slice(0, limit);

    return parts.length ? parts : undefined;
};

export const catalogSpecsFromGuarantee = (guarantee?: number | null) =>
    guarantee ? [`با گارانتی ${guarantee.toLocaleString('fa-IR')} ماهه`] : undefined;

export const resolveCatalogSpecs = ({
    specs,
    attributes,
    subtitle,
    guarantee
}: {
    specs?: string[];
    attributes?: AttributeItem[] | null;
    subtitle?: string | null;
    guarantee?: number | null;
}) => {
    if (specs?.length) {
        return specs;
    }

    const fromAttributes = catalogSpecsFromAttributes(attributes);
    if (fromAttributes?.length) {
        return fromAttributes;
    }

    const fromSubtitle = catalogSpecsFromSubtitle(subtitle);
    const fromGuarantee = catalogSpecsFromGuarantee(guarantee);

    if (fromSubtitle?.length || fromGuarantee?.length) {
        return [...(fromSubtitle ?? []), ...(fromGuarantee ?? [])].slice(0, MAX_CARD_SPECS);
    }

    return undefined;
};

export const catalogItemFromProduct = (product: ProductType & Partial<ProductBatteryType>, options?: FromProductOptions): CatalogProductCardItem => {
    const detailsHref = `/product/${product.uuid}/${product.slug}`;

    return {
        uuid: product.uuid,
        slug: product.slug,
        title: product.title,
        brandName: product.brand?.name ?? '',
        price: product.price,
        sellingPrice: product.sellingPrice,
        discountPercentage: product.discountPercentage,
        available: product.available,
        categoryId: options?.categoryId ?? product.category?.id,
        categoryName: product.category?.name,
        isBattery: isBatteryProduct(product),
        detailsHref,
        buyHref: getProductBuyHref(product, { categoryId: options?.categoryId ?? product.category?.id }),
        subtitle: product.subTitle,
        attributes: product.attributes,
        guarantee: (product as Partial<ProductBatteryType>).guarantee
    };
};

export const catalogItemFromOffer = (offer: OfferContent, href?: string): CatalogProductCardItem => {
    const slug = offer.productSlug || 'product';
    const detailsHref = href ?? `/product/${offer.productUuid}/${slug}`;

    return {
        uuid: offer.productUuid,
        slug,
        title: offer.productTitle,
        brandName: offer.brandName ?? '',
        price: offer.price,
        sellingPrice: offer.sellingPrice,
        discountPercentage: offer.discountPercentage,
        available: offer.offerId != null && (offer.availableQuantity ?? 0) > 0 && offer.price != null,
        availableQuantity: offer.availableQuantity,
        categoryId: offer.categoryId,
        isBattery: isBatteryCategoryId(offer.categoryId),
        detailsHref,
        buyHref: detailsHref,
        subtitle: offer.subtitle,
        guarantee: offer.guarantee
    };
};
